import {
  OFFLINE_PARENT_LIMIT,
  OFFLINE_PUSH_LIMIT,
  offlineMaxAgeMs,
  uuidv7,
  type OfflineCommand,
  type OfflineCommandResult,
  type OfflineCommandType,
  type OfflineDependency,
  type OfflineEntityInfo,
  type OfflinePullRequest,
  type OfflinePullResult,
  type OfflineRecord,
} from '@nexus/core';
import { ApiError } from '../api/client';
import { importDeviceKey, seal, sealBytes, unseal, unsealBytes, type Sealed } from './crypto';
import {
  deleteStore,
  forgetIdentity,
  knownDatabase,
  OfflineStore,
  rememberIdentity,
  type ChangeState,
  type Identity,
  type StoredChange,
  type StoredUpload,
  type UploadState,
} from './store';

/** What the engine needs from the API client; tests pass a fake. */
export interface OfflineApi {
  get<T>(path: string, options?: { timeout?: number }): Promise<T>;
  post<T>(path: string, body?: unknown, options?: { timeout?: number }): Promise<T>;
  /** Raw bytes to the server: a file from the upload queue. */
  put<T>(path: string, body: Blob, options: { headers: Record<string, string> }): Promise<T>;
  /** Raw bytes from the server: a file the device keeps. */
  blob(path: string): Promise<Blob>;
}

/** A record as the app shows it: the server state with the person's open changes on top. */
export interface LocalRecord {
  id: string;
  version: number;
  updatedAt: string;
  data: Record<string, unknown>;
  /** Changed on the device and not yet on the server. */
  pending: boolean;
}

/** A change in the queue, for the sync panel. */
export interface OfflineChange {
  id: string;
  entity: string;
  recordId: string;
  type: OfflineCommandType | null;
  state: ChangeState;
  reason: string | null;
  at: string | null;
  changes: Record<string, unknown>;
  /** Server state at a conflict; `null` when the record is gone there. */
  current: OfflineRecord | null;
}

export interface OfflineEntity extends OfflineEntityInfo {
  count: number;
  lastSync: Date | null;
}

/** A file on the device that waits for its upload. */
export interface OfflineUpload {
  fileId: string;
  entity: string;
  recordId: string;
  /** Empty once it expired on the device: the name went with the content. */
  name: string;
  size: number;
  contentType: string;
  /** `waiting` for network or for records it builds on, `failed` refused by the server. */
  state: UploadState | 'uploading';
  /** Code of the last failure, e.g. `file-too-large`, or of a passing one it waits out. */
  error: string | null;
}

/** A record prepared for working offline (`OfflineEntityInfo.pins`). */
export interface OfflinePrepared {
  entity: string;
  recordId: string;
  /** Prepared by the app because the person opened it, not by the person (`prepare` with `auto`). */
  auto: boolean;
  preparing: boolean;
  /**
   * A sync went through since it was prepared, so what belongs to it is on the device. `false`
   * when the connection broke off meanwhile: the next sync with network brings it.
   */
  synced: boolean;
  /** Files of the record and of what belongs to it: on the device, and in all. */
  files: { done: number; total: number };
  /** Bytes of those files on the device. */
  bytes: number;
}

export interface OfflineState {
  /** The local data is loaded (or there is none). */
  ready: boolean;
  syncing: boolean;
  lastSync: Date | null;
  entities: readonly OfflineEntity[];
  changes: readonly OfflineChange[];
  records: ReadonlyMap<string, readonly LocalRecord[]>;
  prepared: readonly OfflinePrepared[];
  uploads: readonly OfflineUpload[];
  /** Files on the device; `full` when a file did not fit any more. */
  storage: { files: number; bytes: number; full: boolean };
}

/** A file for the upload queue. */
export interface QueuedFile {
  /** UUIDv7; the app may name the file in a record before the upload. Generated when missing. */
  fileId?: string;
  /** Record the file belongs to, e.g. `core.findings` and the finding's ID. */
  entity: string;
  recordId: string;
  file: Blob;
  /** Needed when `file` is no `File`. */
  name?: string;
  /** Records whose creation must reach the server first; the record itself always counts. */
  dependsOn?: readonly OfflineDependency[];
}

interface ChangeBody {
  type: OfflineCommandType;
  baseVersion: number | null;
  changes: Record<string, unknown>;
  base?: Record<string, unknown>;
  at: string;
  dependsOn?: OfflineDependency[];
  current?: OfflineRecord | null;
}

interface QueueItem {
  stored: StoredChange & { seq: number };
  body: ChangeBody | null;
}

interface UploadMeta {
  name: string;
  type: string;
  size: number;
  dependsOn: OfflineDependency[];
  at: string;
}

interface UploadItem {
  stored: StoredUpload;
  meta: UploadMeta | null;
}

interface EntityMeta {
  cursor: string | null;
  lastSync: number | null;
  lastFull: number | null;
  /** Module entities: prepared records (`entity:id`) whose records came already. */
  pinned?: string[];
  /** Kernel data: parent records (`entity:id`) whose data came already. */
  parents?: string[];
}

interface Pin {
  entity: string;
  recordId: string;
  /** See `OfflinePrepared.synced`. */
  synced?: boolean;
  /** See `OfflinePrepared.auto`. */
  auto?: boolean;
  /** When the app last asked for an automatic preparation, in milliseconds. */
  usedAt?: number;
}

export interface PrepareOptions {
  /**
   * Prepared by the app, e.g. an open inspection when the person opens it. At most
   * `AUTO_PREPARED_LIMIT` such records stay, each until `AUTO_PREPARED_DAYS` after it was last
   * opened, and none the person removed before.
   */
  auto?: boolean;
}

/** What the files API answers to an upload. */
interface UploadedFile extends Record<string, unknown> {
  id: string;
  createdAt: string;
}

type PullOptions = Omit<OfflinePullRequest, 'entity' | 'cursor'>;

export class OfflineUnavailableError extends Error {
  constructor() {
    super('offline-unavailable');
  }
}

/** The device has no room for another file. */
export class OfflineStorageFullError extends Error {
  constructor() {
    super('device-storage-full');
  }
}

/** The engine was paused while a request ran: its answer must not reach this store. */
class SyncStoppedError extends Error {
  constructor() {
    super('sync-stopped');
  }
}

const EMPTY: OfflineState = {
  ready: false,
  syncing: false,
  lastSync: null,
  entities: [],
  changes: [],
  records: new Map(),
  prepared: [],
  uploads: [],
  storage: { files: 0, bytes: 0, full: false },
};

/** A full pull replaces what the device holds, so records that left the scope go. */
const FULL_EVERY_MS = 12 * 3_600_000;
/**
 * A sync request without an answer in this time counts as without network, so a sync on weak
 * network ends instead of hanging; the next one continues where this one stopped.
 */
const SYNC_TIMEOUT_MS = 60_000;
const SYNC = { timeout: SYNC_TIMEOUT_MS };
const PUSH_ROUNDS = 20;
/** Records the app prepares by itself (`PrepareOptions.auto`): how many, and for how long. */
export const AUTO_PREPARED_LIMIT = 5;
export const AUTO_PREPARED_DAYS = 14;
/** Records whose automatic preparation the person removed; the app does not prepare them again. */
const DECLINED_LIMIT = 200;
/** The kernel source of the file lists (docs/architecture/files.md, Offline). */
const FILES = 'core.files';
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const recordContext = (entity: string, id: string) => `record:${entity}:${id}`;
const changeContext = (id: string) => `change:${id}`;
const blobContext = (fileId: string) => `blob:${fileId}`;
const uploadContext = (fileId: string) => `upload:${fileId}`;
const keyOf = (entity: string, id: string) => `${entity}:${id}`;
const isNetworkError = (error: unknown) => error instanceof TypeError;
const isQuotaError = (error: unknown) =>
  error instanceof DOMException && error.name === 'QuotaExceededError';

/** Entity lists of older servers and devices lack the newer fields. */
function complete(info: OfflineEntityInfo): OfflineEntityInfo {
  const known: Partial<OfflineEntityInfo> = info;
  return {
    ...info,
    parents: known.parents ?? [],
    pins: known.pins ?? [],
    references: known.references ?? [],
    fileFields: known.fileFields ?? [],
  };
}

/**
 * File IDs at a path of `fileFields` in the data of a record: `fileId`, `photos[].fileId`,
 * `images[]`. Only UUIDs count, they end up in a URL.
 */
export function filesAt(data: unknown, path: string): string[] {
  let values: unknown[] = [data];
  for (const part of path.split('.')) {
    const many = part.endsWith('[]');
    const name = many ? part.slice(0, -2) : part;
    values = values.flatMap((value) => {
      const next =
        value && typeof value === 'object' && !Array.isArray(value)
          ? (value as Record<string, unknown>)[name]
          : undefined;
      return many ? (Array.isArray(next) ? (next as unknown[]) : []) : [next];
    });
  }
  return values.filter((v): v is string => typeof v === 'string' && UUID.test(v));
}

/** The parent record that kernel data names in `data.entity` and `data.recordId`. */
function parentOf(data: Record<string, unknown>): Pin | null {
  const { entity, recordId } = data;
  return typeof entity === 'string' && typeof recordId === 'string' ? { entity, recordId } : null;
}

function keysOf(ids: Record<string, string[]>): string[] {
  return Object.entries(ids).flatMap(([entity, list]) => list.map((id) => keyOf(entity, id)));
}

/** The IDs not yet in `known`, by entity. */
function without(ids: Record<string, string[]>, known: readonly string[]) {
  const seen = new Set(known);
  const rest: Record<string, string[]> = {};
  for (const [entity, list] of Object.entries(ids)) {
    const fresh = list.filter((id) => !seen.has(keyOf(entity, id)));
    if (fresh.length) rest[entity] = fresh;
  }
  return rest;
}

/** The code of a failure as the API sends it, e.g. `file-too-large`. */
function codeOf(error: ApiError): string {
  if (/^[a-z][a-z0-9-]{0,63}$/.test(error.message)) return error.message;
  if (error.status === 403) return 'forbidden';
  if (error.status === 404) return 'not-found';
  if (error.status >= 500 || error.status === 408 || error.status === 429) return 'server-error';
  return 'failed';
}

/** Asks the browser to keep the offline data when storage runs low (ADR-0034, Nachtrag). */
function persistStorage(): void {
  if ('storage' in navigator && typeof navigator.storage.persist === 'function') {
    void navigator.storage.persist().catch(() => false);
  }
}

/**
 * The offline sync on the device (ADR-0013, docs/architecture/offline.md): keeps the declared
 * entities and the kernel data that follows them in an encrypted local database, queues changes
 * and uploads made without network and replays them in order, and keeps the files of prepared
 * records. The server checks every change again.
 */
export class OfflineEngine {
  private store: OfflineStore | null = null;
  private key: { keyId: string; key: CryptoKey } | null = null;
  /** The key before a change of key, until everything is sealed with the new one. */
  private previousKey: CryptoKey | null = null;
  private entities: OfflineEntityInfo[] = [];
  private meta = new Map<string, EntityMeta>();
  private server = new Map<string, Map<string, OfflineRecord>>();
  private queue: QueueItem[] = [];
  private pins: Pin[] = [];
  /** `entity:id` of records the person removed after the app prepared them by itself. */
  private declined: string[] = [];
  private preparing = new Set<string>();
  private uploads: UploadItem[] = [];
  private uploading = new Set<string>();
  /** Files on the device with their size, by ID. */
  private blobs = new Map<string, number>();
  private full = false;
  private lastSync: number | null = null;
  private running: Promise<void> | null = null;
  /** Asked for while a sync ran, e.g. for a record just prepared: one more round follows. */
  private again = false;
  private stopped = false;
  private state: OfflineState = EMPTY;
  private readonly listeners = new Set<() => void>();
  /** Session and settings for a start without network, set by the app. */
  snapshot: (() => unknown) | null = null;

  constructor(
    readonly identity: Identity,
    private readonly api: OfflineApi,
    private readonly now: () => number = Date.now,
  ) {}

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  getState = (): OfflineState => this.state;

  private starting: Promise<void> | null = null;

  /** Loads what the device holds from earlier syncs. A sync waits for it. */
  start(): Promise<void> {
    this.starting ??= this.begin();
    return this.starting;
  }

  private async begin(): Promise<void> {
    try {
      if (knownDatabase(this.identity)) await this.load();
    } catch {
      // Unreadable (other key, damaged): start over, the next sync fills it again.
      await this.wipe();
    }
    await this.expire();
    this.emit({ ready: true });
  }

  private async load(): Promise<void> {
    const store = await OfflineStore.open(this.identity);
    this.store = store;
    this.key = (await store.meta<{ keyId: string; key: CryptoKey }>('key')) ?? null;
    if (!this.key) return;
    this.previousKey = (await store.meta<{ key: CryptoKey } | null>('previousKey'))?.key ?? null;
    this.entities = ((await store.meta<OfflineEntityInfo[]>('entities')) ?? []).map(complete);
    this.lastSync = (await store.meta<number>('lastSync')) ?? null;
    this.pins = (await store.meta<Pin[]>('pins')) ?? [];
    this.declined = (await store.meta<string[]>('declined')) ?? [];
    for (const { entity } of this.entities) {
      this.meta.set(entity, (await store.meta<EntityMeta>(`entity:${entity}`)) ?? emptyMeta());
    }
    for (const row of await store.records()) {
      const record = await this.open<OfflineRecord>(recordContext(row.entity, row.id), row.sealed);
      this.serverMap(row.entity).set(record.id, record);
    }
    this.queue = await Promise.all(
      (await store.changes()).map(async (stored) => ({
        stored: stored as QueueItem['stored'],
        body: stored.sealed
          ? await this.open<ChangeBody>(changeContext(stored.id), stored.sealed)
          : null,
      })),
    );
    this.uploads = await Promise.all(
      (await store.uploads()).map(async (stored) => ({
        stored,
        meta: stored.sealed
          ? await this.open<UploadMeta>(uploadContext(stored.fileId), stored.sealed)
          : null,
      })),
    );
    this.blobs = await store.blobSizes();
    if (!this.previousKey) return;
    // The app closed during a change of key: finish it. Until then both keys open the data.
    try {
      await this.persistAll(this.previousKey);
    } catch {
      // E.g. no room for the copies: the next start tries again.
    }
  }

  /** Pushes queued changes and uploads, pulls every entity and fetches files. One at a time. */
  sync(): Promise<void> {
    if (this.stopped) return Promise.resolve();
    if (this.running) {
      this.again = true;
      return this.running;
    }
    this.running = (async () => {
      do {
        this.again = false;
        await this.runSync();
      } while (this.again && !this.stopped);
    })().finally(() => {
      this.running = null;
    });
    return this.running;
  }

  /**
   * Every sync request names account and company; the server refuses it when the session belongs
   * to someone else by now (other company chosen, other login). An answer that arrives after a
   * pause is dropped as well.
   */
  private async request<T>(call: (path: (route: string) => string) => Promise<T>): Promise<T> {
    if (this.stopped) throw new SyncStoppedError();
    const { companyId, accountId } = this.identity;
    const identity = `company=${encodeURIComponent(companyId)}&account=${encodeURIComponent(accountId)}`;
    const result = await call((route) => `${route}${route.includes('?') ? '&' : '?'}${identity}`);
    if (this.stopped) throw new SyncStoppedError();
    return result;
  }

  private async runSync(): Promise<void> {
    await this.start();
    this.emit({ syncing: true });
    // A record prepared while this round runs may miss parts of it; the next round covers it.
    const round = new Set(this.pins.map((p) => keyOf(p.entity, p.recordId)));
    try {
      const entities = (
        await this.request((path) =>
          this.api.get<OfflineEntityInfo[]>(path('/sync/entities'), SYNC),
        )
      ).map(complete);
      if (!entities.length) {
        // Nothing (any longer) to take offline: nothing stays on the device either. Files waiting
        // for their upload go to the server first where they can.
        if (this.key && this.store) await this.upload();
        if (this.store || knownDatabase(this.identity)) await this.wipe();
        return;
      }
      await this.ensureKey();
      await this.push();
      // An upload may free changes that wait for it, e.g. a finding naming its new photo.
      if (await this.upload()) await this.push();
      const removed = this.entities.filter((e) => !entities.some((n) => n.entity === e.entity));
      this.entities = entities;
      await this.store!.setMeta('entities', entities);
      for (const { entity } of removed) await this.dropEntity(entity);
      for (const entity of this.pullOrder()) await this.pull(entity);
      await this.prune();
      await this.dropAutoPins(round);
      await this.download();
      await this.collect();
      await this.markSynced(round);
      this.lastSync = this.now();
      await this.store!.setMeta('lastSync', this.lastSync);
      if (this.snapshot) {
        await this.store!.setMeta(
          'snapshot',
          await seal(this.key!.key, 'snapshot', this.snapshot()),
        );
      }
      rememberIdentity(this.identity);
    } catch (error) {
      // Without network the device keeps working with what it has.
      if (isNetworkError(error)) return;
      // Paused, or the session belongs to another account or company by now.
      if (error instanceof SyncStoppedError) return;
      if (error instanceof ApiError && error.status === 409) return;
      // The session ended: the data stays for the next login of the same account, with its unsent
      // changes, but the app no longer starts without network until then.
      if (error instanceof ApiError && error.status === 401) {
        forgetIdentity(this.identity);
        return;
      }
      throw error;
    } finally {
      await this.expire();
      this.emit({ syncing: false });
    }
  }

  private async ensureKey(): Promise<void> {
    this.store ??= await OfflineStore.open(this.identity);
    let deviceId = await this.store.meta<string>('device');
    if (!deviceId) {
      deviceId = uuidv7();
      await this.store.setMeta('device', deviceId);
    }
    const answer = await this.request((path) =>
      this.api.post<{ keyId: string; key: string }>(path('/sync/key'), { deviceId }, SYNC),
    );
    if (this.key?.keyId === answer.keyId) return;
    // An earlier change of key did not finish: it comes first, so two keys open everything.
    if (this.previousKey) await this.persistAll(this.previousKey);
    const previous = this.key;
    this.key = { keyId: answer.keyId, key: await importDeviceKey(answer.key) };
    // A new key (e.g. rotated on the server): what the device holds gets sealed again with it.
    // The old key stays until then, so an app closed meanwhile still opens everything.
    if (previous) {
      this.previousKey = previous.key;
      await this.store.setMeta('previousKey', previous);
    }
    await this.store.setMeta('key', this.key);
    await this.persistAll(previous?.key);
  }

  private async persistAll(previous: CryptoKey | undefined): Promise<void> {
    const rows = [];
    for (const [entity, records] of this.server) {
      for (const record of records.values()) rows.push(await this.sealRecord(entity, record));
    }
    await this.store!.putRecords(rows);
    await this.store!.putChanges(
      await Promise.all(this.queue.map((item) => this.sealChange(item))),
    );
    await this.store!.putUploads(
      await Promise.all(this.uploads.map((item) => this.sealUpload(item))),
    );
    if (!previous) return;
    // Session and settings for a start without network, also when the sync stops early.
    const snapshot = await this.store!.meta<Sealed>('snapshot');
    let session: unknown;
    try {
      session = snapshot && (await unseal<unknown>(previous, 'snapshot', snapshot));
    } catch {
      // Sealed with the new key already, by an earlier attempt.
    }
    if (session) {
      await this.store!.setMeta('snapshot', await seal(this.key!.key, 'snapshot', session));
    }
    for (const fileId of this.blobs.keys()) {
      const row = await this.store!.blob(fileId);
      if (!row) continue;
      let bytes: ArrayBuffer;
      try {
        bytes = await unsealBytes(previous, blobContext(fileId), row.sealed);
      } catch {
        // Sealed with the new key already, by an earlier attempt.
        continue;
      }
      await this.store!.putBlob({
        ...row,
        sealed: await sealBytes(this.key!.key, blobContext(fileId), bytes),
      });
    }
    this.previousKey = null;
    await this.store!.setMeta('previousKey', null);
  }

  /** Opens a value of the device, during a change of key also one sealed with the old key. */
  private async open<T>(context: string, sealed: Sealed): Promise<T> {
    try {
      return await unseal<T>(this.key!.key, context, sealed);
    } catch (error) {
      if (!this.previousKey) throw error;
      return unseal<T>(this.previousKey, context, sealed);
    }
  }

  private async sealRecord(entity: string, record: OfflineRecord) {
    return {
      entity,
      id: record.id,
      sealed: await seal(this.key!.key, recordContext(entity, record.id), record),
    };
  }

  private async sealChange(item: QueueItem): Promise<StoredChange> {
    item.stored.sealed = item.body
      ? await seal(this.key!.key, changeContext(item.stored.id), item.body)
      : null;
    return item.stored;
  }

  private async sealUpload(item: UploadItem): Promise<StoredUpload> {
    item.stored.sealed = item.meta
      ? await seal(this.key!.key, uploadContext(item.stored.fileId), item.meta)
      : null;
    return item.stored;
  }

  /**
   * Whether something builds on a record that has not reached the server yet: its creation is
   * still in the queue (in any state), or, for `core.files`, its upload.
   */
  private waits(dependencies: readonly OfflineDependency[], except?: QueueItem): boolean {
    return dependencies.some((d) =>
      d.entity === FILES
        ? this.uploads.some((u) => u.stored.fileId === d.recordId)
        : this.queue.some(
            (q) =>
              q !== except &&
              q.body?.type === 'create' &&
              q.stored.entity === d.entity &&
              q.stored.recordId === d.recordId,
          ),
    );
  }

  private async push(): Promise<void> {
    for (let round = 0; round < PUSH_ROUNDS; round++) {
      // One change per record and round: the next builds on the version the server returns.
      // A change whose dependency is not on the server yet waits, and the later ones with it.
      const seen = new Set<string>();
      const batch: QueueItem[] = [];
      for (const item of this.queue) {
        const record = keyOf(item.stored.entity, item.stored.recordId);
        if (seen.has(record)) continue;
        seen.add(record);
        if (item.stored.state !== 'pending' || !item.body) continue;
        if (!this.waits(item.body.dependsOn ?? [], item)) batch.push(item);
      }
      if (!batch.length) return;
      const sent = batch.slice(0, OFFLINE_PUSH_LIMIT);
      const commands = sent.map((item): OfflineCommand => ({
        id: item.stored.id,
        entity: item.stored.entity,
        type: item.body!.type,
        recordId: item.stored.recordId,
        baseVersion: item.body!.baseVersion,
        changes: item.body!.changes,
        ...(item.body!.base ? { base: item.body!.base } : {}),
        ...(item.body!.dependsOn?.length ? { dependsOn: item.body!.dependsOn } : {}),
        at: item.body!.at,
      }));
      const { results } = await this.request((path) =>
        this.api.post<{ results: OfflineCommandResult[] }>(path('/sync/push'), { commands }, SYNC),
      );
      const applied = await this.applyResults(results);
      this.emitData();
      if (!applied) return;
    }
  }

  private async applyResults(results: readonly OfflineCommandResult[]): Promise<number> {
    let applied = 0;
    const changedRecords = [];
    const removedRecords: [string, string][] = [];
    const removedChanges: number[] = [];
    const updatedChanges: QueueItem[] = [];
    for (const result of results) {
      const item = this.queue.find((q) => q.stored.id === result.id);
      if (!item?.body) continue;
      const { entity, recordId } = item.stored;
      if (result.status === 'applied') {
        applied++;
        this.queue = this.queue.filter((q) => q !== item);
        removedChanges.push(item.stored.seq);
        const records = this.serverMap(entity);
        const before = records.get(recordId);
        if (item.body.type === 'delete') {
          records.delete(recordId);
          removedRecords.push([entity, recordId]);
        } else {
          const record: OfflineRecord = {
            id: recordId,
            version: result.version,
            updatedAt: item.body.at,
            deleted: false,
            data: { ...before?.data, ...item.body.changes },
          };
          records.set(recordId, record);
          changedRecords.push(await this.sealRecord(entity, record));
        }
        // The next change to the record was made on top of this one.
        const next = this.queue.find(
          (q) => q.stored.entity === entity && q.stored.recordId === recordId && q.body,
        );
        if (next?.body) {
          next.body.baseVersion = result.version;
          updatedChanges.push(next);
        }
      } else if (result.status === 'conflict') {
        item.stored.state = 'conflict';
        item.body.current = result.current;
        updatedChanges.push(item);
      } else if (result.status === 'rejected') {
        item.stored.state = 'rejected';
        item.stored.reason = result.reason;
        updatedChanges.push(item);
      }
    }
    await this.store!.putRecords(changedRecords, removedRecords);
    await this.store!.putChanges(
      await Promise.all(updatedChanges.map((item) => this.sealChange(item))),
      removedChanges,
    );
    return applied;
  }

  /**
   * Sends waiting uploads whose record and dependencies reached the server, oldest first, with
   * the ID the file got on the device: a repeated upload gets the stored file. Returns how many
   * went through. A refusal stays visible with its reason; the file stays until the person
   * discards it.
   */
  private async upload(): Promise<number> {
    let done = 0;
    for (const item of [...this.uploads]) {
      const { stored, meta } = item;
      if (stored.state !== 'waiting' || !meta) continue;
      const target = { entity: stored.entity, recordId: stored.recordId };
      if (this.waits([target, ...meta.dependsOn])) continue;
      const content = await this.file(stored.fileId);
      if (!content) {
        await this.setUpload(item, 'failed', 'file-missing');
        continue;
      }
      const query = new URLSearchParams(target).toString();
      this.uploading.add(stored.fileId);
      this.emitData();
      try {
        const file = await this.request((path) =>
          this.api.put<UploadedFile>(path(`/files/${stored.fileId}?${query}`), content, {
            headers: { 'X-File-Name': encodeURIComponent(meta.name) },
          }),
        );
        await this.uploaded(item, file);
        done++;
      } catch (error) {
        // No network, session gone or meant for someone else: the sync as a whole stops.
        if (
          !(error instanceof ApiError) ||
          error.status === 401 ||
          (error.status === 409 && error.message !== 'file-exists')
        ) {
          throw error;
        }
        // Storage or scanner down: the file waits for the next sync. Anything else is final.
        const passing = error.status >= 500 || error.status === 408 || error.status === 429;
        await this.setUpload(item, passing ? 'waiting' : 'failed', codeOf(error));
      } finally {
        this.uploading.delete(stored.fileId);
        this.emitData();
      }
    }
    return done;
  }

  private async uploaded(item: UploadItem, file: UploadedFile): Promise<void> {
    this.uploads = this.uploads.filter((u) => u !== item);
    await this.store!.putUploads([], [item.stored.fileId]);
    // Listed on the device right away; the next pull of the file list confirms it.
    if (this.entities.find((e) => e.entity === FILES)?.parents.includes(item.stored.entity)) {
      await this.storeServer(FILES, {
        id: file.id,
        version: 1,
        updatedAt: file.createdAt,
        deleted: false,
        data: { ...file },
      });
    }
  }

  private async setUpload(item: UploadItem, state: UploadState, error: string | null) {
    item.stored = { ...item.stored, state };
    if (error) item.stored.error = error;
    else delete item.stored.error;
    await this.store!.putUploads([item.stored]);
  }

  /**
   * Pull order: kernel data after the entities it follows, referenced records after the records
   * that name them, e.g. objects after inspections.
   */
  private pullOrder(): string[] {
    const order: string[] = [];
    const rest = [...this.entities];
    const after = new Map(
      this.entities.map((info) => [info.entity, [...info.parents, ...this.referrers(info)]]),
    );
    while (rest.length) {
      const ready = rest.findIndex((info) =>
        after
          .get(info.entity)!
          .every((p) => order.includes(p) || !rest.some((r) => r.entity === p)),
      );
      const [next] = rest.splice(Math.max(ready, 0), 1);
      order.push(next!.entity);
    }
    return order;
  }

  /**
   * IDs of prepared records an entity pins, by pin entity. Records of an entity that prepares
   * itself also count when records on the device name them, e.g. the object of an inspection.
   */
  private pinIds(info: OfflineEntityInfo): Record<string, string[]> {
    const ids: Record<string, string[]> = {};
    for (const pin of info.pins) {
      const list = this.pins.filter((p) => p.entity === pin.entity).map((p) => p.recordId);
      if (pin.entity === info.entity) list.push(...this.referenced(info));
      if (list.length) ids[pin.entity] = [...new Set(list)].slice(0, OFFLINE_PARENT_LIMIT);
    }
    return ids;
  }

  /** Fields of other entities that name records of this one: pin fields and `references`. */
  private referringFields(info: OfflineEntityInfo): { entity: string; field: string }[] {
    if (!info.pins.some((p) => p.entity === info.entity)) return [];
    return this.entities.flatMap((other) =>
      other.entity === info.entity
        ? []
        : [...other.pins, ...other.references]
            .filter((p) => p.entity === info.entity && p.field !== 'id')
            .map((p) => ({ entity: other.entity, field: p.field })),
    );
  }

  /** Entities whose records name records of this one. */
  private referrers(info: OfflineEntityInfo): string[] {
    return [...new Set(this.referringFields(info).map((f) => f.entity))];
  }

  /** IDs of records of an entity that records on the device name, changes included. */
  private referenced(info: OfflineEntityInfo): string[] {
    const ids: string[] = [];
    for (const { entity, field } of this.referringFields(info)) {
      for (const record of this.view(entity)) {
        const value = record.data[field];
        for (const id of Array.isArray(value) ? value : [value]) {
          if (typeof id === 'string' && UUID.test(id)) ids.push(id);
        }
      }
    }
    return ids;
  }

  /** IDs of the parent records the device got from the server, by parent entity. */
  private parentIds(info: OfflineEntityInfo): Record<string, string[]> {
    const ids: Record<string, string[]> = {};
    for (const parent of info.parents) {
      const list = [...(this.server.get(parent)?.keys() ?? [])];
      if (list.length) ids[parent] = list.slice(0, OFFLINE_PARENT_LIMIT);
    }
    return ids;
  }

  private async pull(entity: string): Promise<void> {
    const info = this.entities.find((e) => e.entity === entity)!;
    const meta = this.meta.get(entity) ?? emptyMeta();
    const now = this.now();
    let full = !meta.cursor || !meta.lastFull || now - meta.lastFull > FULL_EVERY_MS;
    const options: PullOptions = {};
    let known: string[] = [];
    let fresh: PullOptions | null = null;
    if (info.pins.length) {
      options.pins = this.pinIds(info);
      known = keysOf(options.pins);
      // A record is no longer prepared: a full pull takes its records off the device.
      if ((meta.pinned ?? []).some((k) => !known.includes(k))) full = true;
      const added = without(options.pins, meta.pinned ?? []);
      if (Object.keys(added).length) fresh = { pins: added, pinsOnly: true };
    } else if (info.parents.length) {
      options.parents = this.parentIds(info);
      known = keysOf(options.parents);
      const added = without(options.parents, meta.parents ?? []);
      if (Object.keys(added).length) fresh = { parents: added };
    }
    const records = this.serverMap(entity);
    const seen = new Set<string>();
    let cursor = full ? null : meta.cursor;
    try {
      // Records just prepared, or new parents: everything of them, wherever the cursor stands.
      if (fresh && !full) await this.pullPages(entity, fresh, null);
      cursor = await this.pullPages(entity, options, cursor, full ? seen : undefined);
    } catch (error) {
      // The person may no longer take the entity offline.
      if (error instanceof ApiError && error.status === 403) {
        await this.dropEntity(entity);
        return;
      }
      throw error;
    }
    if (full) {
      const gone = [...records.keys()].filter((id) => !seen.has(id));
      for (const id of gone) records.delete(id);
      await this.store!.putRecords(
        [],
        gone.map((id) => [entity, id]),
      );
    }
    const next: EntityMeta = {
      cursor,
      lastSync: this.now(),
      lastFull: full ? now : meta.lastFull,
      ...(info.pins.length ? { pinned: known } : {}),
      ...(info.parents.length ? { parents: known } : {}),
    };
    this.meta.set(entity, next);
    await this.store!.setMeta(`entity:${entity}`, next);
    this.emitData();
  }

  /** Pages of a pull from `from` on; returns where the next pull continues. */
  private async pullPages(
    entity: string,
    options: PullOptions,
    from: string | null,
    seen?: Set<string>,
  ): Promise<string> {
    const records = this.serverMap(entity);
    let cursor = from;
    for (;;) {
      const page = await this.request((path) =>
        this.api.post<OfflinePullResult>(path('/sync/pull'), { entity, cursor, ...options }, SYNC),
      );
      const rows = [];
      const removed: [string, string][] = [];
      for (const record of page.records) {
        seen?.add(record.id);
        if (record.deleted) {
          records.delete(record.id);
          removed.push([entity, record.id]);
        } else {
          records.set(record.id, record);
          rows.push(await this.sealRecord(entity, record));
        }
      }
      await this.store!.putRecords(rows, removed);
      if (!page.more) return page.cursor;
      cursor = page.cursor;
    }
  }

  /** Whether the device holds a record, as received from the server or created here. */
  private holds(entity: string, id: string): boolean {
    return (
      !!this.server.get(entity)?.has(id) ||
      this.queue.some(
        (q) =>
          q.stored.entity === entity &&
          q.stored.recordId === id &&
          q.body?.type === 'create' &&
          q.stored.state !== 'rejected',
      )
    );
  }

  /**
   * Kernel data whose parent record left the device goes with it, parents first; so does kernel
   * data of an entity it no longer follows, e.g. files after the read permission went.
   */
  private async prune(): Promise<void> {
    if (!this.store) return;
    const removed: [string, string][] = [];
    for (const entity of this.pullOrder()) {
      const info = this.entities.find((e) => e.entity === entity)!;
      const records = this.server.get(entity);
      if (!info.parents.length || !records) continue;
      for (const record of [...records.values()]) {
        const parent = parentOf(record.data);
        if (!parent) continue;
        if (info.parents.includes(parent.entity) && this.holds(parent.entity, parent.recordId)) {
          continue;
        }
        records.delete(record.id);
        removed.push([entity, record.id]);
      }
    }
    if (removed.length) await this.store.putRecords([], removed);
  }

  /**
   * What belongs to which record (`entity:id`): module records to the prepared records they
   * point to (`pins`), kernel data to its parent record.
   */
  private children(): Map<string, string[]> {
    const children = new Map<string, string[]>();
    const add = (parent: string, child: string) => {
      const list = children.get(parent);
      if (list) list.push(child);
      else children.set(parent, [child]);
    };
    for (const info of this.entities) {
      if (!info.pins.length && !info.parents.length) continue;
      for (const record of this.server.get(info.entity)?.values() ?? []) {
        const own = keyOf(info.entity, record.id);
        for (const pin of info.pins) {
          // The record prepared itself is no child of itself.
          if (pin.entity === info.entity) continue;
          const value = record.data[pin.field];
          if (typeof value === 'string') add(keyOf(pin.entity, value), own);
        }
        const parent = info.parents.length ? parentOf(record.data) : null;
        if (parent) add(keyOf(parent.entity, parent.recordId), own);
      }
    }
    return children;
  }

  /**
   * Prepared records and what belongs to them, e.g. the inspections of a prepared object, their
   * findings and the files of both. A record prepared itself, e.g. an inspection, also brings the
   * kernel data of the records it points to, e.g. the plans of its object, but not the other
   * records that point there.
   */
  private belonging(pins: readonly Pin[], children = this.children()): Set<string> {
    const belongs = new Set<string>();
    const open = pins.map((p) => keyOf(p.entity, p.recordId));
    const kernel = new Set(this.entities.filter((e) => e.owner === 'core').map((e) => e.entity));
    for (const pin of pins) {
      for (const target of this.references(pin)) {
        open.push(
          ...(children.get(target) ?? []).filter((child) =>
            kernel.has(child.slice(0, child.lastIndexOf(':'))),
          ),
        );
      }
    }
    for (let key = open.pop(); key !== undefined; key = open.pop()) {
      if (belongs.has(key)) continue;
      belongs.add(key);
      open.push(...(children.get(key) ?? []));
    }
    return belongs;
  }

  /**
   * Records a record prepared itself points to with its other pin fields, e.g. the object of an
   * inspection (`entity:id`). Empty for records of entities that cannot be prepared themselves.
   */
  private references(pin: Pin): string[] {
    const info = this.entities.find((e) => e.entity === pin.entity);
    if (!info?.pins.some((p) => p.entity === info.entity)) return [];
    const data = this.dataOf(pin.entity, pin.recordId);
    if (!data) return [];
    return info.pins.flatMap((p) => {
      const value = data[p.field];
      return p.entity !== info.entity && typeof value === 'string' ? [keyOf(p.entity, value)] : [];
    });
  }

  /** Data of a record as the device shows it, with changes not yet on the server. */
  private dataOf(entity: string, id: string): Record<string, unknown> | null {
    let data = this.server.get(entity)?.get(id)?.data ?? null;
    for (const { stored, body } of this.queue) {
      if (stored.entity !== entity || stored.recordId !== id || !body) continue;
      if (stored.state === 'rejected') continue;
      data = body.type === 'delete' ? null : { ...data, ...body.changes };
    }
    return data;
  }

  /** Files named in `fileFields` by the given records. */
  private filesOf(records: Iterable<string>): Set<string> {
    const files = new Set<string>();
    const named = new Map(
      this.entities.filter((e) => e.fileFields.length).map((e) => [e.entity, e]),
    );
    for (const key of records) {
      const at = key.lastIndexOf(':');
      const info = named.get(key.slice(0, at));
      const record = info && this.server.get(info.entity)?.get(key.slice(at + 1));
      if (!record) continue;
      for (const path of info.fileFields) {
        for (const id of filesAt(record.data, path)) files.add(id);
      }
    }
    return files;
  }

  /**
   * The files the device keeps: those of prepared records and what belongs to them, and those of
   * kernel data without parents, e.g. images of document templates, which every visit needs.
   */
  private keep(): Set<string> {
    const files = this.filesOf(this.belonging(this.pins));
    for (const info of this.entities) {
      if (info.owner !== 'core' || info.parents.length || !info.fileFields.length) continue;
      for (const record of this.server.get(info.entity)?.values() ?? []) {
        for (const path of info.fileFields) {
          for (const id of filesAt(record.data, path)) files.add(id);
        }
      }
    }
    return files;
  }

  /** Fetches the files the device keeps and does not hold yet, one by one. */
  private async download(): Promise<void> {
    this.full = false;
    for (const fileId of this.keep()) {
      if (this.blobs.has(fileId)) continue;
      let content: Blob;
      try {
        content = await this.request((path) => this.api.blob(path(`/files/${fileId}/content`)));
      } catch (error) {
        // This file only, e.g. removed on the server meanwhile: the next sync tries again.
        if (error instanceof ApiError && error.status !== 401 && error.status !== 409) continue;
        throw error;
      }
      try {
        await this.storeBlob(fileId, await content.arrayBuffer(), content.type);
      } catch (error) {
        if (!isQuotaError(error)) throw error;
        this.full = true;
        return;
      } finally {
        this.emitData();
      }
    }
  }

  private async storeBlob(fileId: string, bytes: ArrayBuffer, type: string, upload?: StoredUpload) {
    const sealed = await sealBytes(this.key!.key, blobContext(fileId), bytes);
    await this.store!.putBlob({ fileId, size: bytes.byteLength, type, sealed }, upload);
    this.blobs.set(fileId, bytes.byteLength);
  }

  /** Removes files the device no longer keeps. Files waiting for their upload always stay. */
  private async collect(): Promise<void> {
    if (!this.store) return;
    const keep = this.keep();
    for (const item of this.uploads) keep.add(item.stored.fileId);
    const gone = [...this.blobs.keys()].filter((id) => !keep.has(id));
    if (!gone.length) return;
    await this.store.deleteBlobs(gone);
    for (const id of gone) this.blobs.delete(id);
    this.emitData();
  }

  /** Prepared records of a round that went through: what belongs to them is on the device. */
  private async markSynced(round: ReadonlySet<string>): Promise<void> {
    const done = (p: Pin) => round.has(keyOf(p.entity, p.recordId));
    if (!this.pins.some((p) => !p.synced && done(p))) return;
    this.pins = this.pins.map((p) => (done(p) ? { ...p, synced: true } : p));
    await this.store!.setMeta('pins', this.pins);
  }

  /** The person may no longer take the entity offline. */
  private dropEntity(entity: string): Promise<void> {
    return this.retire(entity, 'forbidden');
  }

  /**
   * Data older than `maxAge` of its entity leaves the device (ADR-0013).
   */
  private async expire(): Promise<void> {
    if (!this.store) return;
    const now = this.now();
    let expired = false;
    for (const info of this.entities) {
      const meta = this.meta.get(info.entity);
      const maxAge = offlineMaxAgeMs(info.maxAge);
      if (!meta?.lastSync || maxAge === null || now - meta.lastSync <= maxAge) continue;
      await this.retire(info.entity, 'expired');
      expired = true;
    }
    if (expired) {
      await this.prune();
      await this.collect();
    }
    this.emitData();
  }

  /**
   * Removes the records of an entity from the device. Its unsent changes and uploads stay
   * visible as refused, without their content, so the person knows what did not reach the
   * server.
   */
  private async retire(entity: string, reason: string): Promise<void> {
    this.server.delete(entity);
    this.meta.set(entity, emptyMeta());
    if (!this.store) return;
    const items = this.queue.filter((q) => q.stored.entity === entity);
    await this.store.clearEntity(entity);
    await this.store.setMeta(`entity:${entity}`, emptyMeta());
    for (const item of items) {
      const { seq: _seq, ...rest } = item.stored;
      item.body = null;
      const stored: StoredChange = {
        ...rest,
        state: 'rejected',
        reason: item.stored.state === 'rejected' ? (item.stored.reason ?? reason) : reason,
        sealed: null,
      };
      item.stored = { ...stored, seq: await this.store.addChange(stored) };
    }
    const uploads = this.uploads.filter((u) => u.stored.entity === entity && u.meta);
    for (const item of uploads) {
      item.meta = null;
      item.stored = { ...item.stored, state: 'failed', error: reason, sealed: null };
    }
    if (uploads.length) {
      await this.store.putUploads(uploads.map((u) => u.stored));
      await this.store.deleteBlobs(uploads.map((u) => u.stored.fileId));
      for (const item of uploads) this.blobs.delete(item.stored.fileId);
    }
    this.emitData();
  }

  /** A change made on the device. Returns the ID of the record. */
  async change(
    entity: string,
    type: OfflineCommandType,
    recordId: string | null,
    changes: Record<string, unknown> = {},
    options: { dependsOn?: readonly OfflineDependency[] } = {},
  ): Promise<string> {
    const info = this.entities.find((e) => e.entity === entity);
    if (!info?.writable || !this.key || !this.store) throw new OfflineUnavailableError();
    const id = recordId ?? uuidv7();
    const view = this.view(entity).find((r) => r.id === id);
    if (type !== 'create' && !view) throw new OfflineUnavailableError();
    if (type === 'create' && (view || !UUID.test(id))) throw new Error('invalid-record-id');
    const body: ChangeBody = {
      type,
      baseVersion: type === 'create' ? null : (this.server.get(entity)?.get(id)?.version ?? 0),
      changes: type === 'delete' ? {} : changes,
      ...(type === 'update' && view
        ? { base: Object.fromEntries(Object.keys(changes).map((k) => [k, view.data[k] ?? null])) }
        : {}),
      at: new Date(this.now()).toISOString(),
      ...(options.dependsOn?.length ? { dependsOn: [...options.dependsOn] } : {}),
    };
    const stored: StoredChange = {
      id: uuidv7(),
      entity,
      recordId: id,
      state: 'pending',
      sealed: null,
    };
    stored.sealed = await seal(this.key.key, changeContext(stored.id), body);
    const seq = await this.store.addChange(stored);
    this.queue.push({ stored: { ...stored, seq }, body });
    // Created on this device, e.g. an inspection in the basement: it stays prepared, else the
    // first pull after the upload would take it off again, as it is prepared nowhere else.
    if (type === 'create' && info.pins.some((p) => p.entity === entity)) {
      if (!this.pins.length) persistStorage();
      this.pins = [...this.pins, { entity, recordId: id }];
      await this.store.setMeta('pins', this.pins);
    }
    this.emitData();
    return id;
  }

  /** Conflict: apply the person's change on top of the server state after all. */
  async retry(changeId: string): Promise<void> {
    const item = this.queue.find((q) => q.stored.id === changeId);
    if (!item?.body || item.stored.state !== 'conflict') return;
    const current = item.body.current;
    if (current && !current.deleted) {
      item.body.baseVersion = current.version;
      if (item.body.base) {
        item.body.base = Object.fromEntries(
          Object.keys(item.body.base).map((k) => [k, current.data[k] ?? null]),
        );
      }
      await this.storeServer(item.stored.entity, current);
    }
    delete item.body.current;
    item.stored.state = 'pending';
    await this.store!.putChanges([await this.sealChange(item)]);
    this.emitData();
  }

  /** Drops a change that conflicted or was refused; the device takes the server state. */
  async discard(changeId: string): Promise<void> {
    const item = this.queue.find((q) => q.stored.id === changeId);
    if (!item || item.stored.state === 'pending') return;
    this.queue = this.queue.filter((q) => q !== item);
    await this.store!.putChanges([], [item.stored.seq]);
    const current = item.body?.current;
    if (current !== undefined) {
      if (current && !current.deleted) await this.storeServer(item.stored.entity, current);
      else {
        this.serverMap(item.stored.entity).delete(item.stored.recordId);
        await this.store!.putRecords([], [[item.stored.entity, item.stored.recordId]]);
      }
      const next = this.queue.find(
        (q) => q.stored.entity === item.stored.entity && q.stored.recordId === item.stored.recordId,
      );
      if (next?.body && current) {
        next.body.baseVersion = current.version;
        await this.store!.putChanges([await this.sealChange(next)]);
      }
    } else {
      // Refused: the next full pull brings the server state back.
      const meta = this.meta.get(item.stored.entity);
      if (meta) this.meta.set(item.stored.entity, { ...meta, lastFull: null });
    }
    this.emitData();
  }

  private async storeServer(entity: string, record: OfflineRecord): Promise<void> {
    this.serverMap(entity).set(record.id, record);
    await this.store!.putRecords([await this.sealRecord(entity, record)]);
  }

  /**
   * Keeps a file on the device and uploads it once there is network and the records it belongs
   * to reached the server. The bytes are copied at once: a file picked on a phone may not be
   * readable later. Returns the file ID.
   */
  async queueUpload(input: QueuedFile): Promise<string> {
    // Only for records the device keeps: their maximum age also limits how long the file stays.
    if (!this.key || !this.store || !this.entities.some((e) => e.entity === input.entity)) {
      throw new OfflineUnavailableError();
    }
    const fileId = input.fileId ?? uuidv7();
    const name = input.name ?? (input.file instanceof File ? input.file.name : '');
    if (!UUID.test(fileId) || !name || this.uploads.some((u) => u.stored.fileId === fileId)) {
      throw new Error('invalid-upload');
    }
    const bytes = await input.file.arrayBuffer();
    const item: UploadItem = {
      stored: {
        fileId,
        entity: input.entity,
        recordId: input.recordId,
        state: 'waiting',
        order: Math.max(0, ...this.uploads.map((u) => u.stored.order)) + 1,
        sealed: null,
      },
      meta: {
        name,
        type: input.file.type || 'application/octet-stream',
        size: bytes.byteLength,
        dependsOn: [...(input.dependsOn ?? [])],
        at: new Date(this.now()).toISOString(),
      },
    };
    try {
      await this.storeBlob(fileId, bytes, item.meta!.type, await this.sealUpload(item));
    } catch (error) {
      if (isQuotaError(error)) throw new OfflineStorageFullError();
      throw error;
    }
    this.uploads.push(item);
    this.emitData();
    return fileId;
  }

  /** A refused upload goes into the queue again, e.g. after the scanner was down. */
  async retryUpload(fileId: string): Promise<void> {
    const item = this.uploads.find((u) => u.stored.fileId === fileId);
    if (!item?.meta || item.stored.state !== 'failed') return;
    await this.setUpload(item, 'waiting', null);
    this.emitData();
  }

  /** The person gives up an upload; its file leaves the device unless kept anyway. */
  async discardUpload(fileId: string): Promise<void> {
    const item = this.uploads.find((u) => u.stored.fileId === fileId);
    if (!item || this.uploading.has(fileId)) return;
    this.uploads = this.uploads.filter((u) => u !== item);
    await this.store!.putUploads([], [fileId]);
    await this.collect();
    this.emitData();
  }

  /** The content of a file on the device, `null` when the device does not hold it. */
  async file(fileId: string): Promise<Blob | null> {
    if (!this.key || !this.store || !this.blobs.has(fileId)) return null;
    const row = await this.store.blob(fileId);
    if (!row) return null;
    let bytes: ArrayBuffer;
    try {
      bytes = await unsealBytes(this.key.key, blobContext(fileId), row.sealed);
    } catch (error) {
      if (!this.previousKey) throw error;
      bytes = await unsealBytes(this.previousKey, blobContext(fileId), row.sealed);
    }
    return new Blob([bytes], { type: row.type });
  }

  /** Whether the device holds the content of a file. */
  hasFile(fileId: string): boolean {
    return this.blobs.has(fileId);
  }

  /**
   * Prepares a record for working offline, e.g. an object before a visit: the records that point
   * to it and their kernel data and files come to the device right away (`pins`). With `auto` the
   * app prepares a record the person opened; a record already prepared stays as it is, one the
   * person removed after an automatic preparation is left out.
   */
  async prepare(entity: string, recordId: string, options: PrepareOptions = {}): Promise<void> {
    const key = keyOf(entity, recordId);
    await this.start();
    if (options.auto && this.declined.includes(key)) return;
    if (!this.store) await this.sync();
    if (!this.store || !this.key) throw new OfflineUnavailableError();
    const existing = this.pins.find((p) => keyOf(p.entity, p.recordId) === key);
    if (existing && options.auto) {
      // Opened again: it stays for longer. The regular syncs keep it up to date.
      if (existing.auto) {
        this.pins = this.pins.map((p) => (p === existing ? { ...p, usedAt: this.now() } : p));
        await this.store.setMeta('pins', this.pins);
        this.emitData();
      }
      return;
    }
    if (existing?.auto) {
      // The person keeps a record the app prepared: from now on it stays until removed.
      this.pins = this.pins.map((p) =>
        p === existing ? { entity, recordId, synced: p.synced } : p,
      );
      await this.store.setMeta('pins', this.pins);
    } else if (!existing) {
      if (!this.pins.length) persistStorage();
      const pin: Pin = options.auto
        ? { entity, recordId, auto: true, usedAt: this.now() }
        : { entity, recordId };
      this.pins = this.limitAuto([...this.pins, pin]);
      await this.store.setMeta('pins', this.pins);
    }
    if (!options.auto && this.declined.includes(key)) {
      this.declined = this.declined.filter((k) => k !== key);
      await this.store.setMeta('declined', this.declined);
    }
    this.preparing.add(key);
    this.emitData();
    try {
      await this.sync();
    } finally {
      this.preparing.delete(key);
      this.emitData();
    }
  }

  /**
   * Ends the preparation: the files leave the device now, the records with the next sync unless
   * the scope keeps them.
   */
  async unprepare(entity: string, recordId: string): Promise<void> {
    const key = keyOf(entity, recordId);
    const pin = this.pins.find((p) => keyOf(p.entity, p.recordId) === key);
    this.pins = this.pins.filter((p) => p !== pin);
    // Removed after the app prepared it: opening the record again does not bring it back.
    if (pin?.auto && !this.declined.includes(key)) {
      this.declined = [...this.declined, key].slice(-DECLINED_LIMIT);
    }
    if (!this.store) return;
    await this.store.setMeta('pins', this.pins);
    await this.store.setMeta('declined', this.declined);
    await this.collect();
    this.emitData();
  }

  /** The most recently opened automatic preparations, at most `AUTO_PREPARED_LIMIT`. */
  private limitAuto(pins: Pin[]): Pin[] {
    const auto = pins
      .filter((p) => p.auto)
      .sort((a, b) => (b.usedAt ?? 0) - (a.usedAt ?? 0))
      .slice(0, AUTO_PREPARED_LIMIT);
    return pins.filter((p) => !p.auto || auto.includes(p));
  }

  /**
   * Automatic preparations end `AUTO_PREPARED_DAYS` after the record was last opened, and when the
   * device no longer holds the record, e.g. because the person may not see it any more.
   */
  private async dropAutoPins(round: ReadonlySet<string>): Promise<void> {
    const cutoff = this.now() - AUTO_PREPARED_DAYS * 86_400_000;
    // Only pins this round pulled for can tell whether the device holds their record.
    const gone = (p: Pin) =>
      round.has(keyOf(p.entity, p.recordId)) && !this.holds(p.entity, p.recordId);
    const kept = this.pins.filter((p) => !p.auto || ((p.usedAt ?? 0) >= cutoff && !gone(p)));
    if (kept.length === this.pins.length) return;
    this.pins = kept;
    await this.store!.setMeta('pins', this.pins);
  }

  /**
   * The person empties the offline storage of this account and company: records, files,
   * preparations and unsent changes. A sync that runs ends first. The next sync fetches what the
   * device holds without preparation again, e.g. catalogs.
   */
  async clear(): Promise<void> {
    const paused = this.stopped;
    // A running sync stops at its next request instead of writing into the emptied store.
    this.stopped = true;
    try {
      await this.running?.catch(() => undefined);
      await this.starting;
      await this.wipe();
    } finally {
      this.stopped = paused;
    }
  }

  /** Removes everything on the device for this account and company. */
  async wipe(): Promise<void> {
    this.store?.close();
    this.store = null;
    this.key = null;
    this.previousKey = null;
    this.entities = [];
    this.meta.clear();
    this.server.clear();
    this.queue = [];
    this.pins = [];
    this.declined = [];
    this.uploads = [];
    this.blobs.clear();
    this.lastSync = null;
    await deleteStore(this.identity);
    this.emitData();
  }

  /** Stops syncing while the app does not show this account and company. */
  pause(): void {
    this.stopped = true;
  }

  resume(): void {
    this.stopped = false;
  }

  private serverMap(entity: string): Map<string, OfflineRecord> {
    let map = this.server.get(entity);
    if (!map) {
      map = new Map();
      this.server.set(entity, map);
    }
    return map;
  }

  private view(entity: string): LocalRecord[] {
    const records = new Map<string, LocalRecord>();
    for (const r of this.server.get(entity)?.values() ?? []) {
      records.set(r.id, {
        id: r.id,
        version: r.version,
        updatedAt: r.updatedAt,
        data: r.data,
        pending: false,
      });
    }
    for (const { stored, body } of this.queue) {
      // Refused changes are not shown; conflicts are, until the person decides.
      if (stored.entity !== entity || !body || stored.state === 'rejected') continue;
      const existing = records.get(stored.recordId);
      if (body.type === 'delete') {
        records.delete(stored.recordId);
      } else {
        records.set(stored.recordId, {
          id: stored.recordId,
          version: existing?.version ?? 0,
          updatedAt: body.at,
          data: { ...existing?.data, ...body.changes },
          pending: true,
        });
      }
    }
    return [...records.values()];
  }

  private emitData(): void {
    this.emit({});
  }

  private emit(patch: Partial<OfflineState>): void {
    const records = new Map(this.entities.map((e) => [e.entity, this.view(e.entity)]));
    let bytes = 0;
    for (const size of this.blobs.values()) bytes += size;
    const children = this.pins.length ? this.children() : new Map<string, string[]>();
    this.state = {
      ...this.state,
      ...patch,
      lastSync: this.lastSync ? new Date(this.lastSync) : null,
      entities: this.entities.map((info) => {
        const lastSync = this.meta.get(info.entity)?.lastSync;
        return {
          ...info,
          count: records.get(info.entity)?.length ?? 0,
          lastSync: lastSync ? new Date(lastSync) : null,
        };
      }),
      changes: this.queue.map(({ stored, body }) => ({
        id: stored.id,
        entity: stored.entity,
        recordId: stored.recordId,
        type: body?.type ?? null,
        state: stored.state,
        reason: stored.reason ?? null,
        at: body?.at ?? null,
        changes: body?.changes ?? {},
        current: body?.current ?? null,
      })),
      records,
      prepared: this.pins.map((pin) => {
        const files = [...this.filesOf(this.belonging([pin], children))];
        const held = files.filter((id) => this.blobs.has(id));
        return {
          entity: pin.entity,
          recordId: pin.recordId,
          auto: !!pin.auto,
          preparing: this.preparing.has(keyOf(pin.entity, pin.recordId)),
          synced: !!pin.synced,
          files: { done: held.length, total: files.length },
          bytes: held.reduce((sum, id) => sum + (this.blobs.get(id) ?? 0), 0),
        };
      }),
      uploads: this.uploads.map(({ stored, meta }) => ({
        fileId: stored.fileId,
        entity: stored.entity,
        recordId: stored.recordId,
        name: meta?.name ?? '',
        size: meta?.size ?? 0,
        contentType: meta?.type ?? 'application/octet-stream',
        state: this.uploading.has(stored.fileId) ? 'uploading' : stored.state,
        error: stored.error ?? null,
      })),
      storage: { files: this.blobs.size, bytes, full: this.full },
    };
    for (const listener of this.listeners) listener();
  }
}

function emptyMeta(): EntityMeta {
  return { cursor: null, lastSync: null, lastFull: null };
}

/**
 * Session and settings from the last sync, for a start without network. `null` when the device
 * holds no offline data for the last account or it has expired.
 */
export async function restoreSnapshot<T>(identity: Identity | null): Promise<T | null> {
  if (!identity || !knownDatabase(identity)) return null;
  let store: OfflineStore | null = null;
  try {
    store = await OfflineStore.open(identity);
    const key = await store.meta<{ key: CryptoKey }>('key');
    const sealed = await store.meta<Sealed>('snapshot');
    const entities = (await store.meta<OfflineEntityInfo[]>('entities')) ?? [];
    const lastSync = await store.meta<number>('lastSync');
    const longest = Math.max(0, ...entities.map((e) => offlineMaxAgeMs(e.maxAge) ?? 0));
    if (!key || !sealed || !lastSync || Date.now() - lastSync > longest) return null;
    try {
      return await unseal<T>(key.key, 'snapshot', sealed);
    } catch (error) {
      // The app closed during a change of key.
      const previous = await store.meta<{ key: CryptoKey } | null>('previousKey');
      if (!previous) throw error;
      return await unseal<T>(previous.key, 'snapshot', sealed);
    }
  } catch {
    return null;
  } finally {
    store?.close();
  }
}
